import type { CoachProfile, RegisterResult } from "@flexv-coach/shared";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, authApi, tokenStore } from "@/lib/api";
import type { RegisterPayload } from "@/lib/api";

interface AuthContextValue {
    coach: CoachProfile | null;
    /** True while the stored session is being validated on first mount. */
    initializing: boolean;
    isAuthenticated: boolean;
    /** Rejects with an `ApiError` carrying the server message. Returns the profile so callers can gate on `emailVerifiedAt`. */
    login: (identifier: string, password: string) => Promise<CoachProfile>;
    /** Exchanges a Google/Firebase ID token for a coach session. */
    loginWithGoogle: (idToken: string) => Promise<CoachProfile>;
    /**
     * Creates the account — OTP-dulu: manual tanpa sesi (return tanpa
     * profile), Google signup langsung verified + sesi. Rejects ApiError.
     */
    register: (payload: RegisterPayload) => Promise<RegisterResult>;
    /** Refreshes the coach profile (e.g. after email verification). */
    refreshProfile: () => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [coach, setCoach] = useState<CoachProfile | null>(null);
    const [initializing, setInitializing] = useState(true);

    useEffect(() => {
        let cancelled = false;

        if (!tokenStore.getAccess()) {
            setInitializing(false);
            return;
        }

        authApi
            .me()
            .then((profile) => {
                if (!cancelled) setCoach(profile);
            })
            .catch((error: unknown) => {
                if (error instanceof ApiError && error.status === 401) {
                    tokenStore.clear();
                }
            })
            .finally(() => {
                if (!cancelled) setInitializing(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const login = useCallback(async (identifier: string, password: string) => {
        const result = await authApi.login(identifier, password);
        setCoach(result.coach);
        return result.coach;
    }, []);

    const loginWithGoogle = useCallback(async (idToken: string) => {
        const result = await authApi.google(idToken);
        setCoach(result.coach);
        return result.coach;
    }, []);

    const register = useCallback(async (payload: RegisterPayload) => {
        // OTP-dulu: tanpa sesi untuk manual (setCoach hanya bila verified).
        // VerifyEmailPage menerbitkan sesi pertama saat OTP lolos.
        const result = await authApi.register(payload);
        if (result.coach) {
            setCoach(result.coach);
        }
        return result;
    }, []);

    const logout = useCallback(async () => {
        await authApi.logout();
        setCoach(null);
    }, []);

    const refreshProfile = useCallback(async () => {
        const profile = await authApi.me();
        setCoach(profile);
    }, []);

    const value = useMemo<AuthContextValue>(
        () => ({
            coach,
            initializing,
            isAuthenticated: coach !== null,
            login,
            loginWithGoogle,
            register,
            refreshProfile,
            logout,
        }),
        [coach, initializing, login, loginWithGoogle, register, refreshProfile, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
    return context;
}
